import { Card } from './cards.interface';
import { AccountingContact, AccountingContactMatch, AccountingTaxRates } from './accounting.interface';

export interface TransactionUpdateRequest {
  category?: number | null;
  contact?: AccountingContact | null;
  receipt?: string;
  items?: TransactionItem[] | null; // will return either category, or items. not both.
  tracking?: number[]; //the selected option id's, not the category id's
  vat?: VatType;
  tax?: number;
  unit_price?: string;
  unit_quantity?: number;
  invoice_number?: string;
  description?: string;
  flag?: boolean;
}

export interface SplitCategorySelectionObject {
  name: string;
  value: number;
  account_code?: string;
  tax_id?: number;
}

export enum VatType {
  EXEMPT = -1,
  ZERO_RATE = 0,
  STANDARD = 1,
  CAPITALGOODS = 2,
  NOVAT = 3,
}

export enum VatTypeLabel {
  EXEMPT = 'Exempt',
  ZERO_RATE = 'Zero rated',
  STANDARD = 'Standard',
  CAPITALGOODS = 'Standard - Capital Goods',
  NOVAT = 'No VAT',
}

export interface TransactionAccount {
  name: string;
  code: string;
  tax_id?: number;
}

export interface TransactionCategory {
  id: number;
  name: string;
  is_visible: boolean;
  account?: TransactionAccount;
  disabled?: boolean; // FE use only for dropdown display
}

export interface TransactionTrackingCategory {
  id: number;
  name: string;
  category: {
    id: number;
    name: string;
  };
}

export interface TransactionItem {
  name: string;
  amount: string | null;
  unit_price?: string;
  unit_quantity?: number;
  category: number;
  vat?: VatType;
  tax?: number; // Sent as ID to API, but received as AccountingTaxRates (see TransactionItemFromAPI)
  tracking?: number[]; // Sent as IDs to API, but received as TransactionTrackingCategory[] (see TransactionItemFromAPI)
}

// TransactionItem as returned from API (category is an object, not just ID)
export interface TransactionItemFromAPI {
  name: string;
  amount: string;
  unit_price?: string;
  unit_quantity?: number;
  category: TransactionCategory;
  tracking?: TransactionTrackingCategory[]; // API returns full objects
  flag: boolean;
  vat?: VatType;
  tax?: AccountingTaxRates;
}

export interface Transaction {
  id: string;
  first_name?: string;
  last_name?: string;
  merchant: string;
  amount: string;
  unit_price?: string;
  unit_quantity?: number;
  timestamp: string;
  status: TransactionStatusLabel;
  card?: Card;
  category?: TransactionCategory; // will return either category, or items. not both.
  contact: AccountingContact;
  contact_match: AccountingContactMatch;
  items?: TransactionItemFromAPI[]; // API returns items with full category objects
  receipt?: string;
  receipt_ai: boolean;
  receipt_status: TransactionReceiptStatus;
  receipt_files?: TransactionReceiptFile[];
  error?: string;
  type: TransactionType;
  vat?: VatType;
  tracking?: TransactionTrackingCategory[];
  tax?: AccountingTaxRates;
  flag: boolean;
  invoice_number?: string;
  description?: string;
}

export interface TransactionReceiptFile {
  created: string;
  id: string;
  name: string;
  size: number;
  status: TransactionReceiptStatus;
  type: FileType;
  url: string;
}

export interface TransactionItemError {
  amount?: string[];
  unit_price?: string[];
  unit_quantity?: number[];
  name?: string[];
  tax_type?: string[];
  category?: string[];
}

// Internal interface for items with tracking ID
export interface TransactionItemWithTracking extends TransactionItem {
  _trackingId: string;
  category_account_code?: string;
}

export interface TransactionItemTemplateDisplay {
  category: SplitCategorySelectionObject;
  items: TransactionItemWithTracking[];
  subtotal: number;
}

export interface TransactionCategoryRequest {
  name: string;
}

export interface TransactionCategoryNameRequest {
  id: number;
  name: string;
}

export interface TransactionCategoryVisibilityRequest {
  id: number;
  is_visible: boolean;
}

export interface TransactionReady {
  id: string;
}

export interface TransactionFilterParams {
  first_name?: string;
  last_name?: string;
  merchant?: string;
  status?: string[];
  receipt?: string;
  date_from: string;
  date_to: string;
  category?: string[];
  contact?: string;
  employee?: string[];
  type?: string;
}

export interface TransactionsSendRemindersRequest {
  biz_id: number;
}

export enum FileType {
  PDF = 'pdf',
  IMAGE = 'image/png',
}

export enum TransactionReceiptStatus {
  PENDING = 10,
  UPLOADED = 20,
  REJECTED = 30,
}

export enum TransactionReceiptStatusLabel {
  PENDING = 'Pending',
  UPLOADED = 'Uploaded',
  REJECTED = 'Rejected',
}

export enum TransactionStatus {
  ERROR = -1,
  NEW = 0,
  READY = 1,
  SYNCHED = 2,
}

export enum TransactionStatusLabel {
  ERROR = 'Error',
  NEW = 'New',
  READY = 'Ready',
  SYNCHED = 'Synched',
}

export enum TransactionType {
  DEPOSIT = 1,
  PAY = 10,
  REFUND = 11,
  CASH_ATM = 20,
  CASH_POS = 21,
}
